// Clinical Decision Support disclaimer constants.
// Must be kept in sync with backend DISCLAIMER_CURRENT_VERSION in server.py.

export const DISCLAIMER_VERSION = "v1-2026-05";
export const DISCLAIMER_LAST_UPDATED = "May 2026";

export const DISCLAIMER_TITLE = "CLINICAL DECISION SUPPORT DISCLAIMER — PLEASE READ CAREFULLY";

export const DISCLAIMER_INTRO =
  "ProsthoAI is an artificial intelligence-powered clinical decision support platform " +
  "designed to assist licensed dental professionals including prosthodontists, general " +
  "dentists, and dental specialists in clinical case analysis and treatment planning. " +
  "By using this platform you acknowledge and agree to the following:";

export const DISCLAIMER_POINTS = [
  {
    title: "Not a substitute for clinical judgment.",
    body:
      "ProsthoAI provides decision support information only. All clinical decisions, " +
      "diagnoses, treatment plans, restorative designs, surgical recommendations, and " +
      "patient care determinations remain the sole and exclusive responsibility of the " +
      "licensed dental professional. No output generated by this platform should be " +
      "implemented without independent clinical evaluation by a qualified licensed practitioner.",
  },
  {
    title: "Not FDA cleared.",
    body:
      "ProsthoAI is not cleared or approved by the U.S. Food and Drug Administration as " +
      "a medical device. It is not intended to diagnose, treat, cure, or prevent any " +
      "disease or dental condition.",
  },
  {
    title: "AI limitations.",
    body:
      "Artificial intelligence systems including ProsthoAI may produce inaccurate, " +
      "incomplete, or outdated clinical information. AI-generated content including case " +
      "analyses, occlusal assessments, implant planning recommendations, material " +
      "selections, and treatment sequencing suggestions must be independently verified by " +
      "the treating dental professional before clinical implementation.",
  },
  {
    title: "Radiographic and clinical examination.",
    body:
      "AI analysis of radiographic images, photographs, and clinical data provided through " +
      "this platform does not replace a comprehensive in-person clinical examination, full " +
      "radiographic series, periodontal assessment, or specialist consultation. Always " +
      "conduct a thorough independent clinical examination before treatment planning.",
  },
  {
    title: "Implant and surgical recommendations.",
    body:
      "Implant placement recommendations, surgical guides, and prosthetic design parameters " +
      "generated by ProsthoAI are for planning assistance only. All surgical and prosthetic " +
      "decisions must be made by the treating clinician based on comprehensive patient " +
      "evaluation including bone quality assessment, medical history review, and informed consent.",
  },
  {
    title: "No dentist-patient relationship.",
    body:
      "Use of ProsthoAI does not create a dentist-patient relationship between CamelionAI, " +
      "its founders, employees, or agents and any patient whose data is entered into the platform.",
  },
  {
    title: "HIPAA and data security.",
    body:
      "You are responsible for ensuring your use of this platform complies with all " +
      "applicable federal and state laws including HIPAA. Do not enter Protected Health " +
      "Information unless your organization has executed a Business Associate Agreement with CamelionAI.",
  },
  {
    title: "Indemnification.",
    body:
      "By using this platform you agree to indemnify and hold harmless CamelionAI LLC, its " +
      "founders, officers, employees, and agents from any claim, liability, loss, or damage " +
      "arising from your use of platform outputs in clinical decision-making.",
  },
  {
    title: "Dental emergency situations.",
    body:
      "ProsthoAI is not designed for use in dental or medical emergency situations. In any " +
      "medical emergency contact emergency services immediately by calling 911.",
  },
  {
    title: "Scope of practice.",
    body:
      "You are solely responsible for ensuring that your use of this platform complies with " +
      "your dental license, specialty credentials, scope of practice, and all applicable " +
      "state dental practice acts and federal regulations.",
  },
  {
    title: "Platform accuracy.",
    body:
      "CamelionAI makes no warranty, express or implied, regarding the accuracy, " +
      "completeness, timeliness, or fitness for purpose of any information provided by this " +
      "platform. Use of this platform is at your own professional risk.",
  },
  {
    title: "Specialist consultation.",
    body:
      "ProsthoAI may suggest specialist referrals or interdisciplinary consultations. These " +
      "suggestions are informational only and do not replace your independent clinical " +
      "judgment regarding the necessity and timing of specialist involvement.",
  },
];

export const DISCLAIMER_FOOTER_LINE =
  `This disclaimer was last updated ${DISCLAIMER_LAST_UPDATED}. ` +
  "CamelionAI LLC — Boca Raton, Florida — Patent Pending US 64/062,147.";

export const FOOTER_BAR_TEXT =
  "ProsthoAI provides dental clinical decision support only. " +
  "All clinical decisions are the sole responsibility of the licensed dental professional. " +
  "Not FDA cleared. Not a substitute for professional clinical judgment.";

export const MODULE_BANNER_TITLE = "Clinical decision support — not a substitute for clinical judgment";
export const MODULE_BANNER_BODY =
  "AI-generated outputs require independent clinical verification by a licensed dental professional " +
  "before implementation. Not FDA cleared. Conduct a full in-person examination before treatment planning.";

export const AI_OUTPUT_BANNER_BODY =
  "This output was generated by an AI clinical decision support system. " +
  "It is NOT a clinical diagnosis or treatment prescription. " +
  "All findings, recommendations, and treatment options must be independently verified by " +
  "the treating licensed dental professional before clinical implementation. " +
  "Not FDA cleared.";

export const PDF_HEADER_TEXT = "NOT FDA CLEARED · CLINICAL DECISION SUPPORT ONLY";
export const PDF_WATERMARK_TEXT = "DRAFT — CLINICIAN REVIEW REQUIRED";
export const PDF_FOOTER_TEXT =
  "AI-generated. Independent verification by a licensed dental professional is required before clinical use. " +
  "Not a substitute for in-person examination. © CamelionAI LLC — Boca Raton, FL — US Patent Pending 64/062,147.";

export const ACK_CHECKBOX_READ_LABEL =
  "I have read and understand the clinical decision support disclaimer.";
export const ACK_CHECKBOX_LICENSED_LABEL =
  "I confirm I am a licensed dental professional and will not use platform outputs as a substitute for independent clinical judgment.";

/**
 * Returns true if the user has acknowledged the **current** version of the disclaimer.
 * Backend is the source of truth (`disclaimer_current_version`); falls back to local const.
 */
export function hasAcknowledgedCurrent(user) {
  if (!user) return false;
  const current = user.disclaimer_current_version || DISCLAIMER_VERSION;
  return Boolean(user.disclaimer_acknowledged_version === current && user.disclaimer_acknowledged_at);
}

// Routes that should show the amber clinical banner in Layout
const CLINICAL_PATH_PREFIXES = [
  "/module/",
  "/quick-assessment",
  "/case-analysis",
  "/treatment-plan",
  "/topology-analysis",
  "/tmd-assessment",
  "/perio-prostho",
  "/consultation",
  "/consultations",
];

export function isClinicalPath(pathname) {
  if (!pathname) return false;
  return CLINICAL_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(p));
}
